import * as React from "react"
import { CalendarDays } from "lucide-react"
import type { DateRange } from "react-day-picker"
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns"

import { cn, getCommissionPeriod, getPreviousCommissionPeriod } from "@/frontend/lib/utils"
import { Button } from "@/frontend/components/ui/button"
import { Calendar } from "@/frontend/components/ui/calendar"
import { t } from "@/shared/lib/th-translations"

interface DateRangePickerProps {
  dateRange?: DateRange
  onDateRangeChange?: (dateRange: DateRange | undefined) => void
  className?: string
  placeholder?: string
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  className,
  placeholder,
}: DateRangePickerProps) {
  const [date, setDate] = React.useState<DateRange | undefined>(dateRange)
  const [isOpen, setIsOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setDate(dateRange)
  }, [dateRange])

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      setDate(undefined)
      if (onDateRangeChange) {
        onDateRangeChange(undefined)
      }
      return
    }

    // If we already have a complete range, clicking any date starts a new range
    if (date?.from && date?.to) {
      // Start fresh with the newly clicked date as start
      const newRange: DateRange = { from: selectedDate, to: undefined }
      setDate(newRange)
      if (onDateRangeChange) {
        onDateRangeChange(newRange)
      }
      return
    }

    // If we only have a start date, the new click becomes the end date
    if (date?.from && !date?.to) {
      const startDate = date.from
      const endDate = selectedDate

      // Ensure start is before end, swap if needed
      const finalRange: DateRange = startDate <= endDate
        ? { from: startDate, to: endDate }
        : { from: endDate, to: startDate }

      setDate(finalRange)
      if (onDateRangeChange) {
        onDateRangeChange(finalRange)
      }
      // Close dropdown when both dates are selected
      setTimeout(() => setIsOpen(false), 150)
      return
    }

    // First click - set as start date
    setDate({ from: selectedDate, to: undefined })
    if (onDateRangeChange) {
      onDateRangeChange({ from: selectedDate, to: undefined })
    }
  }

  const handlePresetClick = (preset: () => DateRange) => {
    const presetRange = preset()
    setDate(presetRange)
    if (onDateRangeChange) {
      onDateRangeChange(presetRange)
    }
    setIsOpen(false)
  }

  // Improved click outside detection
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Generate past commission periods with friendly names
  const generateCommissionPeriods = () => {
    const periods = []
    let currentPeriod = getCommissionPeriod()

    // Generate past 3 commission periods
    for (let i = 0; i < 3; i++) {
      // Capture the period in a closure to avoid reference issues
      const periodToUse = {
        startDate: new Date(currentPeriod.startDate),
        endDate: new Date(currentPeriod.endDate),
        year: currentPeriod.year,
        month: currentPeriod.month,
      }

      // Format as month name and year (e.g., "August 2025")
      const monthName = periodToUse.endDate.toLocaleDateString('en', {
        month: 'long',
        year: 'numeric'
      })

      // Check if this period matches the currently selected date range
      const isCurrentlySelected = date?.from && date?.to &&
        date.from.getTime() === periodToUse.startDate.getTime() &&
        date.to.getTime() === periodToUse.endDate.getTime()

      periods.push({
        label: monthName,
        getValue: () => ({
          from: periodToUse.startDate,
          to: periodToUse.endDate,
        }),
        isCurrent: isCurrentlySelected,
      })
      currentPeriod = getPreviousCommissionPeriod(currentPeriod)
    }

    return periods
  }

  const commissionPeriods = generateCommissionPeriods()

  const calendarPresets = [
    {
      label: "This Month",
      getValue: () => ({
        from: startOfMonth(new Date()),
        to: endOfMonth(new Date()),
      }),
    },
    {
      label: "Last Month",
      getValue: () => {
        const lastMonth = subMonths(new Date(), 1)
        return {
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth),
        }
      },
    },
    {
      label: "Last 3 Months",
      getValue: () => {
        const threeMonthsAgo = subMonths(new Date(), 3)
        return {
          from: startOfMonth(threeMonthsAgo),
          to: endOfMonth(new Date()),
        }
      },
    },
  ]

  return (
    <div className={cn("relative", className)} ref={containerRef}>
      <Button
        variant="outline"
        className={cn(
          "w-[300px] justify-start text-left font-normal",
          !date && "text-gray-500"
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <CalendarDays className="mr-2 h-4 w-4" />
        {date?.from ? (
          date.to ? (
            <>
              {format(date.from, "LLL dd, y")} -{" "}
              {format(date.to, "LLL dd, y")}
            </>
          ) : (
            format(date.from, "LLL dd, y")
          )
        ) : (
          <span>{placeholder || t("datePicker.pickDateRange")}</span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-[800px] max-w-[95vw] bg-white border border-gray-200 rounded-md shadow-lg z-50 p-4 overflow-hidden">
          <h4 className="font-medium text-sm mb-2">Select Date Range</h4>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-gray-500">Click any date to start, then click another date to complete the range.</p>
            {date?.from && !date?.to && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Start date selected</span>
            )}
            {date?.from && date?.to && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Range complete</span>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Side - Calendar */}
            <div className="flex-1 min-w-0">
              <Calendar
                mode="single"
                defaultMonth={date?.from}
                selected={undefined}
                onSelect={handleDateSelect}
                numberOfMonths={1}
                className="w-full"
                disabled={{ before: new Date(2020, 0, 1) }}
                modifiers={{
                  range_start: date?.from ? [date.from] : [],
                  range_end: date?.to ? [date.to] : [],
                  range_middle: date?.from && date?.to ?
                    (() => {
                      const days = []
                      const current = new Date(date.from)
                      current.setDate(current.getDate() + 1)
                      while (current < date.to) {
                        days.push(new Date(current))
                        current.setDate(current.getDate() + 1)
                      }
                      return days
                    })() : []
                }}
                modifiersClassNames={{
                  range_start: "bg-gray-900 text-gray-50 hover:bg-gray-900 hover:text-gray-50 focus:bg-gray-900 focus:text-gray-50 rounded-l-md",
                  range_end: "bg-gray-900 text-gray-50 hover:bg-gray-900 hover:text-gray-50 focus:bg-gray-900 focus:text-gray-50 rounded-r-md",
                  range_middle: "bg-gray-100 text-gray-900 hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-100 focus:text-gray-900 rounded-none"
                }}
              />
            </div>

            {/* Right Side - Period Options */}
            <div className="lg:w-64 w-full space-y-4 border-t lg:border-t-0 lg:border-l pt-4 lg:pt-0 lg:pl-6">
              {/* Commission Periods */}
              <div>
                <h5 className="font-medium text-xs text-gray-700 mb-2 uppercase tracking-wide">Commission Periods</h5>
                <div className="space-y-1">
                  {commissionPeriods.map((period) => (
                    <Button
                      key={period.label}
                      variant={period.isCurrent ? "default" : "ghost"}
                      size="sm"
                      className="w-full justify-start font-normal text-sm hover:bg-gray-50"
                      onClick={() => handlePresetClick(period.getValue)}
                    >
                      {period.label}
                      {period.isCurrent && (
                        <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Current</span>
                      )}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Calendar Months */}
              <div>
                <h5 className="font-medium text-xs text-gray-700 mb-2 uppercase tracking-wide">Calendar Months</h5>
                <div className="space-y-1">
                  {calendarPresets.map((preset) => (
                    <Button
                      key={preset.label}
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start font-normal text-sm hover:bg-gray-50"
                      onClick={() => handlePresetClick(preset.getValue)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}